import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, History, Shield, ArrowRight, Zap, Phone, CreditCard, Waves, Key,
  MessageCircle, Settings, Wallet, LogOut, Receipt, X, FileText,
  RefreshCw, ChevronRight, Printer, Download, Share2, Check, User, Bell, CloudOff, WifiOff, Wifi, Moon, Sun, CheckSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import DashboardSummaryWidget from "@/components/DashboardSummaryWidget";
import { ThemeToggleCompact } from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";
import {
  isOfflineModeValid,
  getOfflineBills,
  getAdminFeeOffline,
  saveOfflinePayment,
  updateBillStatusOffline,
  getTodayDateString,
  getPendingOfflinePayments
} from "@/utils/offlineDB";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Wait for images to load before html2canvas capture
const waitForImagesToLoad = async (element) => {
  const images = element.querySelectorAll('img');
  const promises = Array.from(images).map(img => {
    if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
      setTimeout(resolve, 3000);
    });
  });
  await Promise.all(promises);
  await new Promise(r => setTimeout(r, 300));
};

// Helper to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const allServices = [
  {
    id: "pdam",
    name: "Tagihan PDAM",
    description: "Cek & bayar tagihan PDAM (DigiFlazz)",
    icon: Waves,
    color: "from-sky-500 to-blue-600",
    bgColor: "bg-sky-100",
    textColor: "text-sky-600",
    placeholder: "Masukkan No. Sambungan",
    path: "/pdam-digiflazz",
    active: true,
    directNav: true, // Langsung ke halaman DigiFlazz (punya input sendiri)
    allowedRoles: ["admin", "agen", "petugas", "kasir", "petugas_lapangan"]
  },
  {
    id: "pdam-tebo",
    name: "PDAM Tebo Online",
    description: "Cek & bayar tagihan PDAM Tebo",
    icon: Waves,
    color: "from-cyan-500 to-teal-600",
    bgColor: "bg-cyan-100",
    textColor: "text-cyan-600",
    placeholder: "Masukkan No. Sambungan",
    path: "/pdam-tebo",
    active: true,
    directNav: true, // Langsung navigate tanpa input
    allowedRoles: ["admin", "agen", "petugas", "kasir", "petugas_lapangan"] // Semua role bisa akses (Petugas Lapangan = read-only)
  },
  {
    id: "listrik-token",
    name: "Token Listrik",
    description: "Beli token listrik prabayar",
    icon: Zap,
    color: "from-amber-500 to-orange-600",
    bgColor: "bg-amber-100",
    textColor: "text-amber-600",
    placeholder: "Masukkan No. Meter PLN",
    path: "/listrik/token",
    active: true,
    allowedRoles: ["admin", "agen"] // Hanya admin dan agen
  },
  {
    id: "pulsa",
    name: "Pulsa & Data",
    description: "Isi pulsa dan paket data",
    icon: Phone,
    color: "from-emerald-500 to-teal-600",
    bgColor: "bg-emerald-100",
    textColor: "text-emerald-600",
    placeholder: "Masukkan No. HP",
    path: "/pulsa",
    active: true,
    allowedRoles: ["admin", "agen"] // Hanya admin dan agen
  }
];

// Filter services berdasarkan role user
const getServicesForRole = (role) => {
  // Jika tidak login atau role kosong, tampilkan semua (untuk customer view)
  if (!role) return allServices;
  
  // Filter berdasarkan role
  return allServices.filter(service => 
    service.allowedRoles.includes(role.toLowerCase())
  );
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount || 0);

function getAuthHeader() {
  const token = localStorage.getItem("user_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function LandingPage() {
  // User state - perlu di atas agar bisa digunakan untuk filter services
  const isLoggedIn = !!localStorage.getItem("user_token");
  const userName = localStorage.getItem("user_name") || "";
  const userRole = localStorage.getItem("user_role") || "";
  
  // Get filtered services based on user role
  const services = getServicesForRole(userRole);
  
  const [selectedService, setSelectedService] = useState(services[0]);
  const [customerId, setCustomerId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [userBalance, setUserBalance] = useState(parseFloat(localStorage.getItem("user_balance")) || 0);
  const [showSettings, setShowSettings] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const receiptRef = useRef(null);
  const [pushSubscribed, setPushSubscribed] = useState(localStorage.getItem("user_push_subscribed") === "true");
  
  // Offline mode state for Petugas
  const [offlineModeEnabled, setOfflineModeEnabled] = useState(false);
  const [offlineModeStatus, setOfflineModeStatus] = useState(null);
  const [isDeviceOnline, setIsDeviceOnline] = useState(navigator.onLine);
  const isPetugas = userRole.toLowerCase() === "petugas";
  
  // Auth settings
  const [requireLoginForBills, setRequireLoginForBills] = useState(true);

  // Service status (ON/OFF)
  const [serviceStatus, setServiceStatus] = useState({
    pdam: true,
    pdam_tebo: true,
    token_listrik: true,
    pulsa: true
  });
  const [receiptSettings, setReceiptSettings] = useState(null);

  // Change Password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Check device online status
  useEffect(() => {
    const handleOnline = () => setIsDeviceOnline(true);
    const handleOffline = () => setIsDeviceOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check offline mode status for Petugas
  useEffect(() => {
    const checkOfflineStatus = async () => {
      if (isPetugas && isLoggedIn) {
        const status = await isOfflineModeValid();
        setOfflineModeStatus(status);
        // Auto-enable offline mode if valid
        if (status.valid && !isDeviceOnline) {
          setOfflineModeEnabled(true);
        }
      }
    };
    checkOfflineStatus();
  }, [isPetugas, isLoggedIn, isDeviceOnline]);

  // Fetch service status + receipt settings + auth settings
  useEffect(() => {
    const fetchServiceStatus = async () => {
      try {
        const [statusRes, receiptRes, authRes] = await Promise.all([
          axios.get(`${API}/service-status`),
          axios.get(`${API}/receipt-settings`),
          axios.get(`${API}/auth-settings`)
        ]);
        setServiceStatus(statusRes.data);
        setReceiptSettings(receiptRes.data);
        setRequireLoginForBills(authRes.data.require_login_for_bills !== false);
      } catch (err) {
        console.error("Failed to fetch service status:", err);
      }
    };
    fetchServiceStatus();
  }, []);

  // Subscribe to push notifications for user
  const subscribeToPush = useCallback(async () => {
    if (!isLoggedIn) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log("Push notifications not supported");
      return;
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log("Notification permission denied");
        return;
      }

      // Get VAPID public key from server
      const { data: vapidData } = await axios.get(`${API}/admin/vapid-public-key`);
      const vapidPublicKey = vapidData.publicKey;

      if (!vapidPublicKey) {
        console.log("VAPID key not available");
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      await axios.post(`${API}/user/push-subscribe`, 
        {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
          }
        },
        { headers: getAuthHeader() }
      );

      setPushSubscribed(true);
      localStorage.setItem("user_push_subscribed", "true");
      console.log("Push notification subscribed for user");

    } catch (error) {
      console.error("Push subscription error:", error);
    }
  }, [isLoggedIn]);

  // Check and subscribe to push when logged in
  useEffect(() => {
    if (isLoggedIn && !pushSubscribed) {
      subscribeToPush();
    }
  }, [isLoggedIn, pushSubscribed, subscribeToPush]);

  const fetchBalance = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await axios.get(`${API}/user/balance`, { headers: getAuthHeader() });
      setUserBalance(res.data.balance);
      localStorage.setItem("user_balance", res.data.balance);
    } catch (err) {
      // Do NOT auto-clear tokens on 401. Users only log out via "Keluar" button.
      if (err.response?.status !== 401) {
        // no-op: keep local balance value; will retry on next tick
      }
    }
  }, [isLoggedIn]);

  const fetchTransactions = useCallback(async () => {
    if (!isLoggedIn) return;
    setTxnLoading(true);
    try {
      // Fetch online transactions
      const res = await axios.get(`${API}/user/transactions?limit=30`, { headers: getAuthHeader() });
      const onlineTxns = res.data.transactions || [];
      
      // Fetch pending offline transactions for petugas
      let offlineTxns = [];
      if (isPetugas) {
        const pendingPayments = await getPendingOfflinePayments();
        offlineTxns = pendingPayments.map(p => ({
          id: p.id,
          service_type: 'pdam',
          customer_no: p.customer_no,
          customer_name: p.customer_name,
          product_name: `Tagihan PDAM (Offline)`,
          amount: p.amount,
          admin_fee: p.admin_fee,
          total: p.total,
          status: 'success',
          created_at: p.created_at,
          user_name: localStorage.getItem("user_name") || "",
          user_role: 'petugas',
          is_offline: true,
          synced: p.synced
        }));
      }
      
      // Combine and sort by date (newest first)
      const allTxns = [...onlineTxns, ...offlineTxns].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setTransactions(allTxns);
    } catch {} finally { setTxnLoading(false); }
  }, [isLoggedIn, isPetugas]);

  useEffect(() => {
    if (isLoggedIn) { fetchBalance(); }
  }, [isLoggedIn, fetchBalance]);

  // Reset selected service when role changes (after login/logout)
  useEffect(() => {
    if (services.length > 0 && !services.find(s => s.id === selectedService?.id)) {
      setSelectedService(services[0]);
    }
  }, [services, selectedService]);

  const handleLogout = async () => {
    // Best-effort backend logout to clear active_session (single-device lock)
    const token = localStorage.getItem("user_token");
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (_e) {
        // ignore - still log out locally
      }
    }
    localStorage.removeItem("user_token");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_balance");
    localStorage.removeItem("user_push_subscribed");
    setShowSettings(false);
    toast.success("Berhasil logout");
    window.location.reload();
  };

  const handleChangePassword = async () => {
    if (!resetCode.trim()) {
      toast.error("Masukkan kode reset dari admin");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    
    setIsChangingPassword(true);
    try {
      await axios.post(`${API}/user/change-password-with-code`, {
        reset_code: resetCode.toUpperCase(),
        new_password: newPassword
      }, { headers: getAuthHeader() });
      
      toast.success("Password berhasil diubah!");
      setShowChangePassword(false);
      setResetCode("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Check service status
    const serviceKey = selectedService.id === 'pdam-tebo' ? 'pdam_tebo' : 
                       selectedService.id === 'listrik-token' ? 'token_listrik' : 
                       selectedService.id;
    if (serviceStatus[serviceKey] === false) {
      toast.error("Layanan sedang dalam perbaikan server. Silakan coba lagi nanti.");
      return;
    }
    
    if (!customerId.trim()) {
      toast.error(`Masukkan ${selectedService.placeholder.toLowerCase()}`);
      return;
    }
    if (selectedService.comingSoon) {
      toast.info("Layanan ini akan segera hadir!");
      return;
    }

    // Wajib login untuk Tagihan PDAM dan PDAM Tebo Online
    const isPdamService = selectedService.id === "pdam" || selectedService.id === "pdam-tebo";
    if (isPdamService && requireLoginForBills && !isLoggedIn) {
      toast.error("Silakan login terlebih dahulu untuk melihat tagihan");
      navigate("/login");
      return;
    }
    
    // Cek offline mode untuk Tagihan PDAM
    if (selectedService.id === "pdam" && offlineModeEnabled && isPetugas) {
      // Cek data offline
      setIsLoading(true);
      try {
        const offlineBills = await getOfflineBills(customerId.trim());
        const unpaidBills = offlineBills.filter(b => b.status === 'unpaid');
        
        if (unpaidBills.length > 0) {
          // Navigate with offline flag
          navigate(`${selectedService.path}?id=${customerId.trim()}&offline=true`);
        } else if (offlineBills.length > 0) {
          toast.info("Tidak ada tagihan yang belum dibayar untuk pelanggan ini (Mode Offline)");
        } else {
          toast.error("ID Pelanggan tidak ditemukan dalam data offline. Pastikan data sudah di-download.");
        }
      } catch (error) {
        toast.error("Gagal membaca data offline");
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    setIsLoading(true);
    // PDAM selalu menggunakan data lokal
    navigate(`${selectedService.path}?id=${customerId.trim()}`);
  };

  const openSettingsPanel = () => {
    setShowSettings(true);
    fetchBalance();
    fetchTransactions();
  };

  // Receipt helpers
  const openReceiptFromTxn = (txn) => {
    const svcLabels = { pdam: "Tagihan PDAM", pdam_tebo: "PDAM Tebo Online", token_listrik: "Token Listrik", tagihan_listrik: "Tagihan Listrik", pulsa: "Pulsa" };
    setReceiptData({
      id: txn.id, service_type: txn.service_type,
      service_name: svcLabels[txn.service_type] || txn.service_type,
      customer_no: txn.customer_no, customer_name: txn.customer_name,
      customer_address: txn.customer_address || "",
      product_name: txn.product_name, amount: txn.amount,
      admin_fee: txn.admin_fee, total: txn.total,
      bill_details: txn.bill_details || [],
      cashier_name: txn.user_name, cashier_role: txn.user_role,
      date: new Date(txn.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }),
      status: txn.status === "success" ? "LUNAS" : txn.status
    });
  };

  const generateReceiptText = (data) => {
    const d = "================================";
    const f = (n) => `Rp${(n || 0).toLocaleString("id-ID")}`;
    const headerText = receiptSettings?.header_text || "PRODUK DIGITAL";
    const showFee = receiptSettings?.show_fee_loket !== false;
    const footerText = receiptSettings?.footer_text || "Terima kasih atas pembayaran Anda";
    const noteText = receiptSettings?.note_text || "Struk ini adalah bukti pembayaran yang sah";
    let r = `     ${headerText}\n`;
    r += `     Struk Pembayaran\n${d}\nNo.Trx   : ${(data.id||"").slice(0,8).toUpperCase()}\nTanggal  : ${data.date}\n${d}\n`;
    const isPdam = data.service_type === "pdam" || data.service_type === "pdam_tebo";
    if (isPdam && data.bill_details?.length > 0) {
      r += `No.Samb  : ${data.customer_no}\nNama     : ${data.customer_name}\n`;
      if (data.customer_address) r += `Alamat   : ${data.customer_address}\n`;
      r += `${d}\n`;
      data.bill_details.forEach((b) => {
        const periode = b.bulan || b.periode_text || b.periode || "";
        r += `Periode  : ${periode}\n`;
        if (b.harga_air !== undefined) {
          r += `Denda    : ${f(b.denda)}\nHarga Air: ${f(b.harga_air)}\nAdm      : ${f(b.adm)}\n`;
        } else {
          r += `Tagihan  : ${f(b.total_tagihan)}\n`;
        }
        if (showFee) r += `Biaya Adm: ${f(b.admin_fee)}\n`;
        r += `SUBTOTAL : ${f(b.total || b.total_bayar)}\n${d}\n`;
      });
    } else {
      r += `Layanan  : ${data.service_name}\nNo.Plg   : ${data.customer_no}\n`;
      if (data.customer_name) r += `Nama     : ${data.customer_name}\n`;
      r += `${d}\nProduk   : ${data.product_name}\nHarga    : ${f(data.amount)}\n`;
      if (showFee) r += `Biaya Adm: ${f(data.admin_fee)}\n`;
      r += `${d}\n`;
    }
    r += `TOTAL BAYAR  ${f(data.total)}\n${d}\nStatus   : ${data.status}\nKasir    : ${data.cashier_name}\n${d}\n  ${footerText}\n ${noteText}\n\n\n\n`;
    return r;
  };

  const handlePrintRawBT = () => {
    if (!receiptData) return;
    const text = generateReceiptText(receiptData);
    const base64 = btoa(unescape(encodeURIComponent(text)));
    const link = document.createElement("a");
    link.href = `rawbt:base64,${base64}`;
    link.click();
  };

  const handleDownloadReceipt = async () => {
    if (!receiptRef.current) return;
    try {
      toast.info("Menyiapkan struk...");
      await waitForImagesToLoad(receiptRef.current);
      const canvas = await html2canvas(receiptRef.current, { 
        scale: 2, 
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0
      });
      const link = document.createElement("a");
      link.download = `struk-${(receiptData?.id||"").slice(0,8).toUpperCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Struk diunduh!");
    } catch { toast.error("Gagal mengunduh struk"); }
  };

  const handleShareReceipt = async () => {
    if (!receiptRef.current) return;
    try {
      toast.info("Menyiapkan struk...");
      await waitForImagesToLoad(receiptRef.current);
      const canvas = await html2canvas(receiptRef.current, { 
        scale: 2, 
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0
      });
      canvas.toBlob(async (blob) => {
        if (navigator.share && blob) {
          const file = new File([blob], `struk.png`, { type: "image/png" });
          await navigator.share({ title: "Struk Pembayaran", files: [file] });
        } else { toast.info("Share tidak didukung, gunakan download."); }
      });
    } catch { toast.error("Gagal share struk"); }
  };

  // Summary stats
  const todayStr = new Date().toLocaleDateString("id-ID");
  const todayTxns = transactions.filter(t => new Date(t.created_at).toLocaleDateString("id-ID") === todayStr);
  const todayTotal = todayTxns.reduce((s, t) => s + (t.total || 0), 0);

  const ServiceIcon = selectedService.icon;

  return (
    <div className="min-h-screen hero-gradient water-bg dark:bg-slate-950 dark:bg-none transition-colors duration-300">
      {/* Header */}
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800 dark:text-white">Produk Digital</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <ThemeToggleCompact />
            
            {isLoggedIn ? (
              <>
                <Link to="/history" className="text-slate-600 dark:text-slate-300 hover:text-sky-600 dark:hover:text-sky-400 transition-colors flex items-center gap-1">
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">Riwayat</span>
                </Link>
                <Link to="/transfer-masuk" className="text-slate-600 dark:text-slate-300 hover:text-sky-600 dark:hover:text-sky-400 transition-colors flex items-center gap-1" data-testid="transfer-masuk-link">
                  <Wallet className="w-4 h-4" />
                  <span className="hidden sm:inline">Transfer Masuk</span>
                </Link>
                <button
                  onClick={openSettingsPanel}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors"
                  data-testid="user-settings-btn"
                >
                  <Settings className="w-4 h-4" />
                  <span className="hidden sm:inline">{userName}</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/history" className="text-slate-600 dark:text-slate-300 hover:text-sky-600 dark:hover:text-sky-400 transition-colors flex items-center gap-1">
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">Riwayat</span>
                </Link>
                <Link to="/login" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors" data-testid="login-header-btn">
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Login</span>
                </Link>
              </>
            )}
            <a
              href="https://wa.me/6289512168007"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">CS</span>
            </a>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-6 pt-8 pb-24">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              Bayar Tagihan & Beli
              <span className="block text-sky-600 dark:text-sky-400">Produk Digital</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
              PDAM, Listrik, Pulsa - Semua dalam satu aplikasi. Pembayaran mudah via QRIS.
            </p>
          </motion.div>

          {/* Dashboard Summary Widget - Only for logged in users */}
          {isLoggedIn && (
            <div className="max-w-xl mx-auto mb-6">
              <DashboardSummaryWidget />
            </div>
          )}

          {/* Service Selection */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {/* Login Button - Only show when not logged in */}
            {!isLoggedIn && (
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                whileHover={{ y: -5, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/login')}
                className="service-card relative p-4 rounded-2xl border-2 border-sky-500 bg-gradient-to-br from-sky-50 to-blue-50 shadow-lg text-left hover:shadow-xl transition-all"
                data-testid="dashboard-login-btn"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-2">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-slate-800 text-sm">Login</h3>
                <p className="text-xs text-slate-500 mt-0.5">Masuk ke akun Anda</p>
                <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-sky-500 text-white text-[9px] font-bold rounded-full">
                  MASUK
                </span>
              </motion.button>
            )}
            {services.map((service, index) => {
              const Icon = service.icon;
              const isSelected = selectedService.id === service.id;
              const serviceKey = service.id === 'pdam-tebo' ? 'pdam_tebo' : 
                                 service.id === 'listrik-token' ? 'token_listrik' : 
                                 service.id;
              const isServiceOff = serviceStatus[serviceKey] === false;
              // Add delay offset if login button is showing
              const delayOffset = !isLoggedIn ? 0.15 : 0.1;
              
              return (
                <motion.button
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: delayOffset + index * 0.1, duration: 0.4 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (isServiceOff) {
                      toast.error("Layanan sedang dalam perbaikan server. Silakan coba lagi nanti.");
                      return;
                    }
                    if (service.directNav) {
                      navigate(service.path);
                    } else {
                      setSelectedService(service);
                    }
                  }}
                  className={`service-card relative p-4 rounded-2xl border-2 text-left ${
                    isServiceOff ? 'opacity-60 cursor-not-allowed' :
                    isSelected ? `border-sky-500 bg-white shadow-lg` : `border-transparent bg-white/60 hover:bg-white`
                  }`}
                  data-testid={`service-${service.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${service.bgColor} flex items-center justify-center mb-2 transition-transform duration-300 ${isSelected && !isServiceOff ? 'scale-110' : ''}`}>
                    <Icon className={`w-5 h-5 ${service.textColor}`} />
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm">{service.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{service.description}</p>
                  {isServiceOff && (
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full">
                      PERBAIKAN
                    </span>
                  )}
                  {!isServiceOff && service.directNav && (
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-cyan-500 text-white text-[9px] font-bold rounded-full">
                      ONLINE
                    </span>
                  )}
                  {isSelected && !service.directNav && !isServiceOff && (
                    <motion.div
                      layoutId="selectedIndicator"
                      className="absolute inset-0 border-2 border-sky-500 rounded-2xl"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </motion.button>
              );
            })}
          </motion.div>

          {/* Search Card */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="glass-card rounded-3xl p-6 sm:p-8 max-w-xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${selectedService.color} flex items-center justify-center`}>
                  <ServiceIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-slate-800">{selectedService.name}</h2>
                  <p className="text-sm text-slate-500">{selectedService.description}</p>
                </div>
              </div>

              {/* Offline Mode Toggle - Only for Petugas on Tagihan PDAM */}
              {selectedService.id === "pdam" && isPetugas && isLoggedIn && (
                <div className={`mb-4 p-3 rounded-xl border ${
                  offlineModeEnabled 
                    ? 'bg-amber-50 border-amber-200' 
                    : offlineModeStatus?.valid 
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {offlineModeEnabled ? (
                        <WifiOff className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Wifi className="w-5 h-5 text-emerald-600" />
                      )}
                      <div>
                        <p className={`text-sm font-semibold ${offlineModeEnabled ? 'text-amber-800' : 'text-slate-800'}`}>
                          {offlineModeEnabled ? 'Mode Offline' : 'Mode Online'}
                        </p>
                        {offlineModeStatus?.valid ? (
                          <p className="text-xs text-slate-500">
                            Data offline: {offlineModeStatus.date}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-500">
                            {isDeviceOnline ? 'Terhubung ke internet' : 'Tidak ada koneksi'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Toggle Switch */}
                      {offlineModeStatus?.valid && (
                        <button
                          onClick={() => {
                            setOfflineModeEnabled(!offlineModeEnabled);
                            if (!offlineModeEnabled) {
                              toast.info("Mode Offline diaktifkan. Data dari tanggal " + offlineModeStatus.date);
                            } else {
                              toast.info("Mode Online diaktifkan");
                            }
                          }}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            offlineModeEnabled ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          data-testid="offline-mode-toggle"
                        >
                          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                            offlineModeEnabled ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                      )}
                      {/* Link to Offline Mode page */}
                      <Link 
                        to="/offline-mode" 
                        className="text-xs text-sky-600 hover:text-sky-700 underline"
                      >
                        {offlineModeStatus?.valid ? 'Kelola' : 'Download Data'}
                      </Link>
                    </div>
                  </div>
                  {offlineModeEnabled && (
                    <p className="text-xs text-amber-600 mt-2">
                      Anda menggunakan data offline. Pembayaran akan disinkronkan saat online.
                    </p>
                  )}
                </div>
              )}

              <form onSubmit={handleSearch} className="space-y-4">
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={selectedService.placeholder}
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    className="input-animated h-14 pl-12 pr-4 text-lg rounded-xl border-slate-200 bg-white/80 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10"
                    data-testid="customer-id-input"
                  />
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                </div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    disabled={isLoading || selectedService.comingSoon}
                    className={`btn-animated w-full h-14 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all bg-gradient-to-r ${selectedService.color}`}
                    data-testid="search-btn"
                  >
                    {isLoading ? <div className="spinner w-6 h-6" /> : (
                      <>Cek {selectedService.name}<ArrowRight className="ml-2 w-5 h-5" /></>
                    )}
                  </Button>
                </motion.div>
              </form>

              {selectedService.id === "pdam" && (
                <div className="mt-4 pt-4 border-t border-slate-200 text-center">
                  <Link to="/installment" className="inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700 transition-colors">
                    <Key className="w-4 h-4" />Punya kode cicilan dari admin?
                  </Link>
                </div>
              )}

              {/* Tombol Pengaturan Akun - Untuk semua user yang login */}
              {isLoggedIn && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <button
                    onClick={openSettingsPanel}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 text-white font-medium text-sm hover:from-slate-700 hover:to-slate-800 transition-all"
                    data-testid="settings-btn-below-pdam"
                  >
                    <Settings className="w-4 h-4" />
                    Pengaturan Akun
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Features */}
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
            <div className="glass-card-solid rounded-2xl p-5 text-center">
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mx-auto mb-3">
                <CreditCard className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="font-heading font-semibold text-slate-800 mb-1">Bayar via QRIS</h3>
              <p className="text-sm text-slate-600">Scan dari e-wallet atau mobile banking manapun</p>
            </div>
            <div className="glass-card-solid rounded-2xl p-5 text-center">
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="font-heading font-semibold text-slate-800 mb-1">Aman & Terpercaya</h3>
              <p className="text-sm text-slate-600">Transaksi diverifikasi dan tercatat dengan baik</p>
            </div>
            <div className="glass-card-solid rounded-2xl p-5 text-center">
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mx-auto mb-3">
                <History className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="font-heading font-semibold text-slate-800 mb-1">Bukti ke WhatsApp</h3>
              <p className="text-sm text-slate-600">Bukti pembayaran langsung dikirim ke WA</p>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-6 border-t border-slate-200">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Shield className="w-4 h-4" /><span>Pembayaran aman dan terenkripsi</span>
          </div>
          <p className="text-slate-500 text-sm">© 2025 Produk Digital</p>
        </div>
      </footer>

      {/* ============ SETTINGS PANEL (Slide from right) ============ */}
      <AnimatePresence>
        {showSettings && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowSettings(false)}
            />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white z-50 shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Panel Header */}
              <div className="bg-gradient-to-r from-sky-600 to-blue-700 text-white p-5 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-semibold text-lg">Pengaturan</h2>
                  <button onClick={() => setShowSettings(false)} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-base">{userName}</p>
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full capitalize">{userRole}</span>
                  </div>
                </div>
                {/* Balance Card - Hidden for Petugas since they don't use balance */}
                {userRole.toLowerCase() !== 'petugas' && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sky-100 text-xs">Saldo Anda</p>
                    <p className="text-xl font-heading font-bold" data-testid="settings-balance">{formatCurrency(userBalance)}</p>
                  </div>
                  <button onClick={fetchBalance} className="text-white/70 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
                </div>
                )}
                {/* Top-Up Button - Hidden for Petugas */}
                {userRole.toLowerCase() !== 'petugas' && (
                <Link
                  to="/topup"
                  onClick={() => setShowSettings(false)}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-white/20 hover:bg-white/30 text-white font-medium text-sm transition-all"
                  data-testid="topup-btn"
                >
                  <Wallet className="w-4 h-4" />
                  Tambah Saldo
                </Link>
                )}
              </div>

              {/* Panel Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {/* Today's Summary */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Laporan Hari Ini</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-sky-50 rounded-xl p-3 border border-sky-100">
                      <p className="text-xs text-slate-500">Transaksi</p>
                      <p className="text-lg font-heading font-bold text-sky-600" data-testid="today-txn-count">{todayTxns.length}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                      <p className="text-xs text-slate-500">Total</p>
                      <p className="text-lg font-heading font-bold text-emerald-600" data-testid="today-txn-total">{formatCurrency(todayTotal)}</p>
                    </div>
                  </div>
                  {/* Link to Full Reports */}
                  <Link 
                    to="/my-reports" 
                    className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium text-sm hover:from-sky-600 hover:to-blue-700 transition-all"
                    onClick={() => setShowSettings(false)}
                  >
                    <FileText className="w-4 h-4" />
                    Lihat Laporan Lengkap
                  </Link>
                  
                  {/* Cek Setoran Mandiri (Rekonsiliasi) - Petugas/Kasir/Agen */}
                  {["petugas", "kasir", "agen"].includes(userRole.toLowerCase()) && (
                    <button
                      onClick={() => { setShowSettings(false); navigate("/rekonsiliasi"); }}
                      className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-sky-600 text-white font-medium text-sm hover:from-cyan-600 hover:to-sky-700 transition-all"
                      data-testid="cek-setoran-mandiri-btn"
                    >
                      <CheckSquare className="w-4 h-4" />
                      Cek Setoran Saya
                    </button>
                  )}

                  {/* Mode Offline - Only for Petugas */}
                  {userRole.toLowerCase() === "petugas" && (
                    <Link 
                      to="/offline-mode" 
                      className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-medium text-sm hover:from-amber-600 hover:to-orange-700 transition-all"
                      onClick={() => setShowSettings(false)}
                    >
                      <CloudOff className="w-4 h-4" />
                      Mode Offline
                    </Link>
                  )}
                </div>

                {/* Transaction History */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Riwayat Transaksi</h3>
                  {txnLoading ? (
                    <p className="text-center text-sm text-slate-400 py-4">Memuat...</p>
                  ) : transactions.length === 0 ? (
                    <p className="text-center text-sm text-slate-400 py-4">Belum ada transaksi</p>
                  ) : (
                    <div className="space-y-2">
                      {transactions.slice(0, 20).map((txn) => (
                        <button
                          key={txn.id}
                          onClick={() => openReceiptFromTxn(txn)}
                          className="w-full bg-white border border-slate-100 rounded-xl p-3 flex items-center justify-between hover:border-sky-200 hover:shadow-sm transition-all text-left"
                          data-testid={`txn-${txn.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-800 truncate">
                              {txn.product_name || txn.service_type}
                              {txn.is_offline && !txn.synced && (
                                <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded">Offline</span>
                              )}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{txn.customer_no} - {txn.customer_name}</p>
                            <p className="text-xs text-slate-400">{new Date(txn.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}</p>
                          </div>
                          <div className="text-right ml-3 shrink-0">
                            <p className="font-semibold text-sm text-red-600">-{formatCurrency(txn.total)}</p>
                            <span className="text-xs text-emerald-600">Berhasil</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Panel Footer */}
              <div className="border-t p-4 space-y-2">
                {/* Tombol Ganti Password - untuk semua user kecuali pelanggan Google Auth */}
                <Button 
                  onClick={() => setShowChangePassword(true)} 
                  variant="outline" 
                  className="w-full rounded-xl h-11 gap-2 border-amber-200 text-amber-700 hover:bg-amber-50" 
                  data-testid="change-password-btn"
                >
                  <Key className="w-4 h-4" /> Ganti Password
                </Button>
                <Button onClick={handleLogout} variant="outline" className="w-full rounded-xl h-11 gap-2 border-red-200 text-red-600 hover:bg-red-50" data-testid="settings-logout-btn">
                  <LogOut className="w-4 h-4" /> Keluar
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ============ CHANGE PASSWORD MODAL ============ */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => { setShowChangePassword(false); setResetCode(""); setNewPassword(""); setConfirmPassword(""); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Key className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-800">Ganti Password</h3>
                <p className="text-sm text-slate-500">Gunakan kode dari admin</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-slate-700">
                Untuk mengganti password, Anda harus meminta <span className="font-bold">Kode Reset</span> terlebih dahulu dari Admin.
              </p>
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Kode Reset dari Admin</label>
                <input
                  type="text"
                  placeholder="Masukkan kode 8 karakter"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                  maxLength={8}
                  className="w-full h-12 rounded-xl border border-slate-200 px-4 font-mono text-lg tracking-widest text-center uppercase focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
                  data-testid="reset-code-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Password Baru</label>
                <input
                  type="password"
                  placeholder="Minimal 6 karakter"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Konfirmasi Password Baru</label>
                <input
                  type="password"
                  placeholder="Ketik ulang password baru"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 rounded-full" 
                onClick={() => { setShowChangePassword(false); setResetCode(""); setNewPassword(""); setConfirmPassword(""); }}
              >
                Batal
              </Button>
              <Button
                className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                data-testid="submit-change-password-btn"
              >
                {isChangingPassword ? <div className="spinner w-5 h-5" /> : "Ganti Password"}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ============ RECEIPT MODAL ============ */}
      {receiptData && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={() => setReceiptData(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="relative max-w-md w-full bg-gradient-to-b from-sky-100 to-sky-50 rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto my-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setReceiptData(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center z-10">
              <X className="w-5 h-5 text-slate-500" />
            </button>
            <div className="p-6">
              <div ref={receiptRef} className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-5 mb-6">
                <div className="text-center mb-4 pb-3 border-b border-dashed border-slate-300">
                  <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 mx-auto mb-2 rounded-lg" />
                  <h3 className="text-lg font-heading font-bold text-slate-800">{receiptSettings?.header_text || "PRODUK DIGITAL"}</h3>
                  <p className="text-sm text-slate-500">Struk Pembayaran</p>
                </div>
                <div className="space-y-2 text-sm mb-4 pb-3 border-b border-dashed border-slate-300">
                  <div className="flex justify-between"><span className="text-slate-500">No. Transaksi</span><span className="font-mono font-bold text-slate-800">{(receiptData.id||"").slice(0,8).toUpperCase()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tanggal</span><span className="font-medium text-slate-800 text-right text-xs">{receiptData.date}</span></div>
                </div>
                <div className="space-y-2 text-sm mb-4 pb-3 border-b border-dashed border-slate-300">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{(receiptData.service_type === "pdam" || receiptData.service_type === "pdam_tebo") ? "No. Sambungan" : receiptData.service_type === "pulsa" ? "No. HP" : "No. Pelanggan"}</span>
                    <span className="font-mono font-bold text-slate-800">{receiptData.customer_no}</span>
                  </div>
                </div>

                {/* PDAM / PDAM Tebo with bill details */}
                {(receiptData.service_type === "pdam" || receiptData.service_type === "pdam_tebo") && receiptData.bill_details?.length > 0 ? (
                  <div className="space-y-3 mb-4">
                    {receiptData.bill_details.map((bill, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{receiptData.customer_name}</p>
                            {receiptSettings?.show_customer_address !== false && receiptData.customer_address && <p className="text-xs text-slate-500">{receiptData.customer_address}</p>}
                          </div>
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">LUNAS</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div><p className="text-xs text-slate-400">Periode</p><p className="font-medium text-slate-800 text-sm">{bill.bulan || bill.periode_text || bill.periode}</p></div>
                          {bill.denda !== undefined && (
                            <div className="text-right"><p className="text-xs text-slate-400">Denda</p><p className="font-medium text-red-500 text-sm">{formatCurrency(bill.denda)}</p></div>
                          )}
                        </div>
                        <div className="flex justify-between items-end pt-2 border-t border-slate-200">
                          <div className="text-xs text-slate-500 space-y-0.5">
                            {bill.harga_air !== undefined ? (
                              <>
                                <div>Harga Air: {formatCurrency(bill.harga_air)}</div>
                                <div>Adm: {formatCurrency(bill.adm)}</div>
                              </>
                            ) : (
                              <div>Tagihan: {formatCurrency(bill.total_tagihan)}</div>
                            )}
                            {receiptSettings?.show_fee_loket !== false && (
                              <div className="text-sky-600 font-medium">Biaya Admin: {formatCurrency(bill.admin_fee)}</div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">Subtotal</p>
                            <p className="text-lg font-bold text-sky-600">{formatCurrency(bill.total || bill.total_bayar)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2 text-sm mb-4">
                    {receiptData.customer_name && receiptData.service_type !== "pulsa" && (
                      <div className="flex justify-between"><span className="text-slate-500">Nama</span><span className="font-medium text-slate-800">{receiptData.customer_name}</span></div>
                    )}
                    <div className="flex justify-between"><span className="text-slate-500">Produk</span><span className="font-medium text-slate-800 text-right max-w-[60%] text-xs">{receiptData.product_name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Harga</span><span className="font-medium">{formatCurrency(receiptData.amount)}</span></div>
                    {receiptSettings?.show_fee_loket !== false && (
                      <div className="flex justify-between"><span className="text-sky-600 font-medium">Biaya Admin</span><span className="text-sky-600 font-medium">{formatCurrency(receiptData.admin_fee)}</span></div>
                    )}
                  </div>
                )}

                <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <div><span className="font-semibold text-slate-800">Total Bayar</span><p className="text-xs text-slate-500">Sudah termasuk biaya admin</p></div>
                    <span className="text-xl font-heading font-bold text-sky-600">{formatCurrency(receiptData.total)}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-slate-300">
                  <span className="text-sm text-slate-500">Status</span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 bg-emerald-100 text-emerald-700">{receiptData.status}<Check className="w-4 h-4" /></span>
                </div>
                {receiptSettings?.show_kasir !== false && (
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-slate-300">
                    <span className="text-sm text-slate-500">Kasir</span>
                    <span className="text-sm font-semibold text-slate-800">{receiptData.cashier_name} ({receiptData.cashier_role})</span>
                  </div>
                )}
                <div className="text-center mt-4 pt-3 border-t border-dashed border-slate-300">
                  <p className="text-xs text-slate-500">{receiptSettings?.footer_text || "Terima kasih atas pembayaran Anda"}</p>
                  <p className="text-xs text-slate-400">{receiptSettings?.note_text || "Struk ini adalah bukti pembayaran yang sah"}</p>
                </div>
              </div>

              <div className="space-y-3 mb-3">
                <Button onClick={handlePrintRawBT} className="w-full h-14 rounded-xl gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid="receipt-print-btn">
                  <Printer className="w-5 h-5" /> Print Struk (Bluetooth)
                </Button>
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" onClick={handleDownloadReceipt} className="h-11 rounded-xl gap-2 border-slate-300 text-sm" data-testid="receipt-download-btn"><Download className="w-4 h-4" /> Unduh PNG</Button>
                  <Button variant="outline" onClick={handleShareReceipt} className="h-11 rounded-xl gap-2 border-slate-300 text-sm" data-testid="receipt-share-btn"><Share2 className="w-4 h-4" /> Share</Button>
                </div>
              </div>
              <Button className="w-full rounded-full" variant="ghost" onClick={() => setReceiptData(null)} data-testid="receipt-close-btn">Tutup</Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
